import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { products } from '../data/products';
import { ChevronLeft } from 'lucide-react';
import { useCartStore } from '../store/cart';
import { toast } from 'sonner';
const getColorHex = (colorName: string) => {
  const map: Record<string, string> = {
    Black: '#171717',
    White: '#f5f5f5',
    'Washed Grey': '#525252',
    'Black/Gold': '#262626',
    'Navy/White': '#1e3a8a',
    Olive: '#4d7c0f',
    Cream: '#fef3c7',
    Charcoal: '#3f3f46',
    Burgundy: '#881337',
    'Forest Green': '#14532d',
    Stone: '#d6d3d1',
    Tan: '#d2b48c',
    'Slate Blue': '#475569',
    'Heather Grey': '#9ca3af'
  };
  return map[colorName] || '#000000';
};
export function ProductDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const product = products.find(p => p.id === id);
  const [selectedColor, setSelectedColor] = useState<string>(product?.colorways[0] || '');
  const [selectedSize, setSelectedSize] = useState<string>('');
  const addItem = useCartStore(state => state.addItem);
  if (!product) {
    return <div className="py-32 text-center" data-id="element-334">
        <h2 className="font-display text-4xl mb-4" data-id="element-335">Product Not Found</h2>
        <Link to="/shop" className="text-vsc-gold hover:text-white underline" data-id="element-336">
          Return to Shop
        </Link>
      </div>;
  }
  const handleAddToCart = () => {
    if (!selectedSize || !selectedColor) return;
    addItem(product, selectedColor, selectedSize);
    toast.success(`Added ${product.name} to cart`);
  };
  return <div className="py-12 px-4 max-w-7xl mx-auto w-full" data-id="element-337">
      <Link to="/shop" className="inline-flex items-center text-neutral-400 hover:text-white mb-8 transition-colors" data-id="element-338">
        <ChevronLeft size={20} className="mr-1" data-id="element-339" />
        Back to Shop
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-340">
        {/* Product Image */}
        <div className="bg-neutral-900 rounded-sm overflow-hidden aspect-square lg:aspect-[4/5]" data-id="element-341">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-342" />
        </div>

        {/* Product Info */}
        <div className="flex flex-col" data-id="element-343">
          <h1 className="font-display text-4xl md:text-5xl tracking-wide mb-2" data-id="element-344">
            {product.name}
          </h1>
          <p className="text-2xl text-neutral-300 mb-8" data-id="element-345">${product.price}</p>

          <div className="prose prose-invert mb-10" data-id="element-346">
            <p className="text-neutral-400 leading-relaxed text-lg" data-id="element-347">
              {product.description}
            </p>
          </div>

          {/* Color Selection */}
          <div className="mb-8" data-id="element-348">
            <div className="flex justify-between items-center mb-3" data-id="element-349">
              <span className="font-medium" data-id="element-350">Color</span>
              <span className="text-neutral-400 text-sm" data-id="element-351">{selectedColor}</span>
            </div>
            <div className="flex gap-3" data-id="element-352">
              {product.colorways.map(color => <button key={color} onClick={() => setSelectedColor(color)} className={`w-10 h-10 rounded-full border-2 transition-all ${selectedColor === color ? 'border-vsc-gold scale-110' : 'border-neutral-700 hover:border-neutral-500'}`} style={{
              backgroundColor: getColorHex(color)
            }} aria-label={`Select ${color}`} data-id="element-353" />)}
            </div>
          </div>

          {/* Size Selection */}
          <div className="mb-10" data-id="element-354">
            <div className="flex justify-between items-center mb-3" data-id="element-355">
              <span className="font-medium" data-id="element-356">Size</span>
              <button className="text-neutral-400 text-sm underline hover:text-white" data-id="element-357">
                Size Guide
              </button>
            </div>
            <div className="grid grid-cols-5 gap-3" data-id="element-358">
              {product.sizes.map(size => <button key={size} onClick={() => setSelectedSize(size)} className={`py-3 border rounded-sm transition-colors ${selectedSize === size ? 'border-vsc-gold text-vsc-gold' : 'border-neutral-700 text-neutral-400 hover:border-neutral-500 hover:text-white'}`} data-id="element-359">
                  {size}
                </button>)}
            </div>
          </div>

          {/* Add to Cart */}
          <button onClick={handleAddToCart} className={`w-full py-4 text-lg font-medium rounded-sm transition-colors ${selectedSize ? 'bg-vsc-gold text-black hover:bg-white' : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'}`} disabled={!selectedSize} data-id="element-360">
            {selectedSize ? 'Add to Cart' : 'Select a Size'}
          </button>

          <div className="mt-8 space-y-4 text-sm text-neutral-400 border-t border-neutral-800 pt-8" data-id="element-361">
            <p data-id="element-362">Free shipping on orders over $150.</p>
            <p data-id="element-363">
              14-day return policy. See our{' '}
              <Link to="/terms" className="underline hover:text-white" data-id="element-364">
                Terms of Service
              </Link>{' '}
              for details.
            </p>
          </div>
        </div>
      </div>
    </div>;
}